import React, { FC } from 'react';
import * as Contentful from 'contentful';
import { TypeLink_externalFields } from '@/core/generated-types';
import { ChevronIcon } from '../Icons';
import buildShopSubdomainUrl from '@/utils/shopSubdomain';
import classNames from 'classnames';
import useLocale from '@/hooks/useLocale';

export interface ExternalLinkProps {
  link: Contentful.Entry<TypeLink_externalFields>;
  classes?: string; // Append to the default classes
  children?: React.ReactNode;
  hideArrow?: boolean;
  chevronClasses?: string;
  onClick?: React.MouseEventHandler<any> | undefined;
}

const ExternalLink: FC<ExternalLinkProps> = ({
  link: {
    sys: { id },
    fields: {
      linkText,
      linkUrl,
      blankTarget,
      noFollow,
      hasLeadingArrow,
      hasArrow: hasTrailingArrow,
      hasShopSubdomain,
    },
  },
  classes = '',
  children,
  hideArrow,
  chevronClasses,
  onClick,
}) => {
  const locale = useLocale();
  const href = hasShopSubdomain
    ? buildShopSubdomainUrl(linkUrl, locale)
    : linkUrl;

  return (
    <a
      key={id}
      href={href}
      className={`${classes} flex flex-row items-center gap-1.5`}
      data-testid="link-externalLink"
      rel={noFollow ? 'nofollow' : undefined}
      target={blankTarget ? '_blank' : undefined}
      onClick={onClick}
    >
      {hasLeadingArrow && !hideArrow && (
        <ChevronIcon
          color="indigo"
          direction="left"
          height={13}
          width={13}
          strokeWidth={3}
          classes={classNames('relative top-[1px]', chevronClasses)}
        />
      )}
      {children || linkText}
      {hasTrailingArrow && !hideArrow && (
        <ChevronIcon
          color="indigo"
          direction="right"
          height={13}
          width={13}
          strokeWidth={3}
          classes={classNames('relative top-[1px]', chevronClasses)}
        />
      )}
    </a>
  );
};

export default React.memo(ExternalLink);
